import { Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { EditOrderParking, OrderParkingVM } from '../../../../../models/orderModel';
import { ParkingSiteRowVM, AvailableParking } from '../../../../../models/parkingsite.model';
import { AdminService } from '../../../../../services/admin.service';
import { ParkingService } from '../../../../../services/parking.service';
import { DatePipe } from '@angular/common';
import { ConfirmDeleteComponent } from '../../../users/edit-user/confirm-delete/confirm-delete.component';
import { MatDialog } from '@angular/material/dialog';
import moment from 'moment';
import { MyConfigService } from '../../../../../services/my-config.service';
import { BackOfficeService } from '../../../../../services/backoffice.service';

@Component({
  selector: 'app-back-office24-order-parking-edit',
  templateUrl: './back-office24-order-parking-edit.component.html',
  styleUrl: './back-office24-order-parking-edit.component.css',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})

export class BackOffice24OrderParkingEditComponent implements OnInit, OnDestroy {
  @Input() id: string;
  @Output() saveChangesEvent = new EventEmitter<void>();
  // editOrderParking: EditOrderParking;
  orderParkingVM: OrderParkingVM;
  isDisabled = true;
  isFormNotSaved = true;
  editOrderParkingForm: UntypedFormGroup;
  componentDestroyed$: Subject<boolean> = new Subject();
  parkingFromTime: string;
  parkingToTime: string;
  checkInTime: string;
  checkOutTime: string;
  parkingSiteRowVMs: ParkingSiteRowVM[] = [];
  availableParkingSpots: AvailableParking[];
  isLoading = false;
  activeRequests: number = 0;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { orderParkingId: string, isSubscription: boolean },
    private fb: UntypedFormBuilder,
    public snackBar: MatSnackBar,
    public adminService: AdminService,
    public parkingService: ParkingService,
    public myConfigService: MyConfigService,
    public datepipe: DatePipe,
    public dialog: MatDialog,
    public backOfficeService: BackOfficeService
  ) { }

  ngOnInit(): void {
    if (this.id) {
      this.data.orderParkingId = this.id;
    }
    this.getOrderParking(this.data.orderParkingId);
    this.initEmptyParkingForm();
  }

  private setLoadingState(isLoading: boolean) {
    this.isLoading = isLoading;
  }

  private incrementRequests() {
    this.activeRequests++;
    this.setLoadingState(true);
  }

  private decrementRequests() {
    if (this.activeRequests > 0)
      this.activeRequests--;
    if (this.activeRequests === 0) {
      this.setLoadingState(false);
    }
  }

  initEmptyParkingForm() {
    this.editOrderParkingForm = this.fb.group({
      orderParkingId: [''],
      carId: [''],
      comment: [''],
      parkingFrom: [''],
      parkingFromTime: [''],
      parkingTo: [''],
      parkingToTime: [''],
      parkingSiteId: [''],
      checkIn: [''],
      checkInTime: [''],
      checkOut: [''],
      checkOutTime: ['']
    });
  }

  initEditParkingForm() {
    this.editOrderParkingForm = this.fb.group({
      orderParkingId: [this.orderParkingVM.orderParkingId],
      carId: [this.orderParkingVM.carId],
      comment: [this.orderParkingVM.comment],
      parkingFrom: [this.orderParkingVM.parkingFrom],
      parkingFromTime: [this.parkingFromTime],
      parkingTo: [this.orderParkingVM.parkingTo],
      parkingToTime: [this.parkingToTime],
      parkingSiteId: [this.orderParkingVM.parkingSiteId],
      checkIn: [this.orderParkingVM.checkIn],
      checkInTime: [this.checkInTime],
      checkOut: [this.orderParkingVM.checkOut],
      checkOutTime: [this.checkOutTime]
    });
    const listChange = this.editOrderParkingForm.valueChanges;
    listChange
      .pipe(debounceTime(1000))
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((change: EditOrderParking) => {
        if (!this.isLoading && this.editOrderParkingForm.dirty) {
          this.isDisabled = false;
          this.snackBar.open(`Du har gjort ändringar, glöm inte att spara`, 'Tack!', {
            verticalPosition: 'top',
            duration: 2000
          });
        }
      });
  }

  getOrderParking(orderParkingId: string) {
    this.incrementRequests();
    this.backOfficeService
      .getOrderParking(orderParkingId)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(orderParking => {
        this.setOrderParking(orderParking);
        this.initEditParkingForm();
        this.decrementRequests();
      }, () => {
        this.decrementRequests();
      });
  }

  getAllAvailableParkingSites(parkingId: string) {
    const productId = this.orderParkingVM.productId;
    const fromDate = moment(this.orderParkingVM.parkingFrom).format();
    const toDate = moment(this.orderParkingVM.parkingTo).format();
    this.incrementRequests();

    this.parkingService
      .getAllAvailableParkingSites(parkingId, productId, fromDate, toDate)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(parkingSites => {
        this.decrementRequests();
        parkingSites = this.sortParkingSpotList(parkingSites?.filter((p: any) => p?.parkingSiteActive === false));
        // Add current parking site first in list
        this.availableParkingSpots = [{
          parkingSiteId: this.orderParkingVM.parkingSiteId,
          parkingSiteName: this.orderParkingVM.parkingSiteName,
          bookedFrom: null,
          bookedTo: null,
          inventoryId: null,
          inventoryName: null,
          productId: null,
          productName: null
        }, ...parkingSites];

        this.decrementRequests();
      }, () => {
        this.decrementRequests();
      });
  }

  sortParkingSpotList(parkingList: AvailableParking[]): AvailableParking[] {
    const sortedList = [...parkingList];

    sortedList.sort((a, b) => {
      const processParkingName = (name: string) => {
        const lastColonIndex = name.lastIndexOf(':');

        if (lastColonIndex === -1) {
          const numPart = name.replace(/[^0-9]/g, '') || '0';
          return [numPart, '0'];
        }

        const firstPart = name.substring(0, lastColonIndex).replace(/[^0-9]/g, '') || '0';
        const secondPart = name.substring(lastColonIndex + 1).replace(/[^0-9]/g, '') || '0';
        return [firstPart, secondPart];
      };

      const parkingSiteNameA = processParkingName(a.parkingSiteName);
      const parkingSiteNameB = processParkingName(b.parkingSiteName);

      const firstPartA = +parkingSiteNameA[0];
      const firstPartB = +parkingSiteNameB[0];

      if (firstPartA < firstPartB) return -1;
      if (firstPartA > firstPartB) return 1;

      const secondPartA = +parkingSiteNameA[1] || 0;
      const secondPartB = +parkingSiteNameB[1] || 0;

      if (secondPartA < secondPartB) return -1;
      if (secondPartA > secondPartB) return 1;

      return 0;
    });

    return sortedList;
  }

  saveChanges() {
    this.incrementRequests();
    this.setDateFormats();
    this.parkingService
      .editOrderParking(this.editOrderParkingForm.value)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(orderParkingVM => {
        this.setOrderParking(orderParkingVM);
        this.snackBar.open('Du har sparat ändringarna', 'OK', {
          verticalPosition: 'top',
          duration: 2000
        });
        this.isDisabled = true;
        this.editOrderParkingForm.markAsPristine();
        this.decrementRequests();
        this.saveChangesEvent.emit();
      },
        error => {
          this.decrementRequests();
          this.snackBar.open(`Felkod: ${error.status}`, 'Doh!', {
            verticalPosition: 'top'
          });
        }
      );
  }

  setDateFormats() {
    // console.log(this.editOrderParkingForm.controls['parkingFrom'].value.format('YYYY-MM-DD'));

    try {
      if (moment.isMoment(this.editOrderParkingForm.controls['parkingFrom'].value)) {
        let str: String = moment(this.editOrderParkingForm.controls['parkingFrom'].value).toISOString(true);
        this.editOrderParkingForm.patchValue({
          parkingFrom: str.substring(0, str.indexOf('T', 0)) + 'T' + this.editOrderParkingForm.controls['parkingFromTime'].value
        });
      }
      else {
        let str: String = this.editOrderParkingForm.controls['parkingFrom'].value;
        this.editOrderParkingForm.patchValue({
          parkingFrom: str.substring(0, str.indexOf('T', 0)) + 'T' + this.editOrderParkingForm.controls['parkingFromTime'].value
        });
      }
    } catch (err) {
      console.log(err);
    }


    try {
      if (moment.isMoment(this.editOrderParkingForm.controls['parkingTo'].value)) {
        let str: String = moment(this.editOrderParkingForm.controls['parkingTo'].value).toISOString(true);
        this.editOrderParkingForm.patchValue({
          parkingTo: str.substring(0, str.indexOf('T', 0)) + 'T' + this.editOrderParkingForm.controls['parkingToTime'].value
        });
      }
      else {
        let str: String = this.editOrderParkingForm.controls['parkingTo'].value;
        this.editOrderParkingForm.patchValue({
          parkingTo: str.substring(0, str.indexOf('T', 0)) + 'T' + this.editOrderParkingForm.controls['parkingToTime'].value
        });
      }
    } catch (err) {
      console.log(err);
    }

    try {
      if (moment.isMoment(this.editOrderParkingForm.controls['checkIn'].value)) {
        let str: String = moment(this.editOrderParkingForm.controls['checkIn'].value).toISOString(true);
        this.editOrderParkingForm.patchValue({
          checkIn: str.substring(0, str.indexOf('T', 0)) + 'T' + this.editOrderParkingForm.controls['checkInTime'].value
        });
      }
      else {
        let str: String = this.editOrderParkingForm.controls['checkIn'].value;
        this.editOrderParkingForm.patchValue({
          checkIn: str.substring(0, str.indexOf('T', 0)) + 'T' + this.editOrderParkingForm.controls['checkInTime'].value
        });
      }
    } catch (err) { }

    try {
      if (moment.isMoment(this.editOrderParkingForm.controls['checkOut'].value)) {
        let str: String = moment(this.editOrderParkingForm.controls['checkOut'].value).toISOString(true);
        this.editOrderParkingForm.patchValue({
          checkOut: str.substring(0, str.indexOf('T', 0)) + 'T' + this.editOrderParkingForm.controls['checkOutTime'].value
        });
      }
      else {
        let str: String = this.editOrderParkingForm.controls['checkOut'].value;
        this.editOrderParkingForm.patchValue({
          checkOut: str.substring(0, str.indexOf('T', 0)) + 'T' + this.editOrderParkingForm.controls['checkOutTime'].value
        });
      }
    } catch (err) { }
  }

  setAnyNewParkingSite() {
    const dialogRef = this.dialog.open(ConfirmDeleteComponent, {
      data: {
        message: `Är du säker på att du vill tilldela valfri parkering till ${this.orderParkingVM.carId}?`,
        buttonText: {
          ok: 'Ja',
          cancel: 'Avbryt'
        }
      }
    });
    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.incrementRequests();
        this.parkingService
          .setAnyNewParkingSite(this.data.orderParkingId)
          .pipe(takeUntil(this.componentDestroyed$))
          .subscribe(orderParking => {
            this.setOrderParking(orderParking);
            this.snackBar.open('Du har sparat ändringarna', 'OK', {
              verticalPosition: 'top',
              duration: 2000
            });
            this.decrementRequests();
          },
            error => {
              this.decrementRequests();
              this.snackBar.open(`Felkod: ${error.status}`, 'Doh!', {
                verticalPosition: 'top'
              });
            }
          );
      }
    });
  }

  setOrderParking(orderParkingVM: OrderParkingVM) {
    this.orderParkingVM = orderParkingVM;
    this.parkingFromTime = this.datepipe.transform(this.orderParkingVM.parkingFrom, 'HH:mm');
    this.parkingToTime = this.datepipe.transform(this.orderParkingVM.parkingTo, 'HH:mm');
    this.checkInTime = this.datepipe.transform(this.orderParkingVM.checkIn, 'HH:mm');
    this.checkOutTime = this.datepipe.transform(this.orderParkingVM.checkOut, 'HH:mm');

    this.editOrderParkingForm.controls['parkingFrom'].setValue(moment(this.orderParkingVM.parkingFrom).format('YYYY-MM-DDTHH:mm'), { emitEvent: false });
    this.editOrderParkingForm.controls['parkingTo'].setValue(moment(this.orderParkingVM.parkingTo).format('YYYY-MM-DDTHH:mm'), { emitEvent: false });

    if (this.orderParkingVM.checkIn) {
      this.editOrderParkingForm.controls['checkIn'].setValue(moment(this.orderParkingVM.checkIn).format('YYYY-MM-DDTHH:mm'), { emitEvent: false });
      this.editOrderParkingForm.controls['checkInTime'].setValue(moment(this.orderParkingVM.checkIn).format('HH:mm'), { emitEvent: false });
    }

    if (this.orderParkingVM.checkOut) {
      this.editOrderParkingForm.controls['checkOut'].setValue(moment(this.orderParkingVM.checkOut).format('YYYY-MM-DDTHH:mm'), { emitEvent: false });
      this.editOrderParkingForm.controls['checkOutTime'].setValue(moment(this.orderParkingVM.checkOut).format('HH:mm'), { emitEvent: false });
    }

    if (this.orderParkingVM.parkingId) {
      this.getAllAvailableParkingSites(this.orderParkingVM.parkingId);
    }
  }

  checkInParking() {
    const checkIn = new Date().toLocaleString('sv-SE', {
      timeZone: 'Europe/Stockholm',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    }).replace(' ', 'T');

    const dialogRef = this.dialog.open(ConfirmDeleteComponent, {
      data: {
        message: `Är du säker på att du vill checka in ${this.orderParkingVM.carId}?`,
        buttonText: {
          ok: 'Ja',
          cancel: 'Avbryt'
        }
      }
    });
    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.incrementRequests();
        this.parkingService
          .setStatusCheckIn(this.data.orderParkingId, checkIn)
          .pipe(takeUntil(this.componentDestroyed$))
          .subscribe(orderParking => {
            this.setOrderParking(orderParking);
            this.snackBar.open('Bilen är incheckad', 'OK', {
              verticalPosition: 'top',
              duration: 2000
            });
            this.saveChangesEvent.emit();
            this.decrementRequests();
          },
            error => {
              this.decrementRequests();
              this.snackBar.open(`Felkod: ${error.status}`, 'Doh!', {
                verticalPosition: 'top'
              });
            });
      }
    });
  }

  checkOutParking() {
    const checkOut = new Date().toLocaleString('sv-SE', {
      timeZone: 'Europe/Stockholm',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    }).replace(' ', 'T');

    const dialogRef = this.dialog.open(ConfirmDeleteComponent, {
      data: {
        message: `Är du säker på att du vill checka ut ${this.orderParkingVM.carId}?`,
        buttonText: {
          ok: 'Ja',
          cancel: 'Avbryt'
        }
      }
    });
    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.incrementRequests();
        this.parkingService
          .setStatusCheckOut(this.data.orderParkingId, checkOut)
          .pipe(takeUntil(this.componentDestroyed$))
          .subscribe(orderParking => {
            this.setOrderParking(orderParking);
            this.snackBar.open('Bilen är utcheckad', 'OK', {
              verticalPosition: 'top',
              duration: 2000
            });
            this.saveChangesEvent.emit();
            this.decrementRequests();
          },
            error => {
              this.decrementRequests();
              this.snackBar.open(`Felkod: ${error.status}`, 'Doh!', {
                verticalPosition: 'top'
              });
            });
      }
    });
  }

  setNoShow() {
    const dialogRef = this.dialog.open(ConfirmDeleteComponent, {
      data: {
        message: `Vill du sätta status NoShow på ${this.orderParkingVM.carId}?`,
        buttonText: {
          ok: 'Ja',
          cancel: 'Avbryt'
        }
      }
    });
    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.incrementRequests();
        this.parkingService
          .setStatusNoShow(this.data.orderParkingId)
          .pipe(takeUntil(this.componentDestroyed$))
          .subscribe(orderParking => {
            this.setOrderParking(orderParking);
            this.snackBar.open('Statusen är ändrad till NoShow', 'OK', {
              verticalPosition: 'top',
              duration: 2000
            });
            this.saveChangesEvent.emit();
            this.decrementRequests();
          },
            error => {
              this.decrementRequests();
              this.snackBar.open(`Felkod: ${error.status}`, 'Doh!', {
                verticalPosition: 'top'
              });
            });
      }
    });
  }

  resetCheckIn() {
    const dialogRef = this.dialog.open(ConfirmDeleteComponent, {
      data: {
        message: `Vill du återställa statusen för incheckning på ${this.orderParkingVM.carId}?`,
        buttonText: {
          ok: 'Ja',
          cancel: 'Avbryt'
        }
      }
    });
    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.incrementRequests();
        this.parkingService
          .resetCheckIn(this.data.orderParkingId)
          .pipe(takeUntil(this.componentDestroyed$))
          .subscribe(orderParking => {
            this.setOrderParking(orderParking);
            this.snackBar.open('Statusen för incheckning är återställd', 'OK', {
              verticalPosition: 'top',
              duration: 2000
            });
            this.saveChangesEvent.emit();
            this.decrementRequests();
          },
            error => {
              this.decrementRequests();
              this.snackBar.open(`Felkod: ${error.status}`, 'Doh!', {
                verticalPosition: 'top'
              });
            });
      }
    });
  }

  resetCheckOut() {
    const dialogRef = this.dialog.open(ConfirmDeleteComponent, {
      data: {
        message: `Vill du återställa statusen för utcheckning på ${this.orderParkingVM.carId}?`,
        buttonText: {
          ok: 'Ja',
          cancel: 'Avbryt'
        }
      }
    });
    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.incrementRequests();
        this.parkingService
          .resetCheckOut(this.data.orderParkingId)
          .pipe(takeUntil(this.componentDestroyed$))
          .subscribe(orderParking => {
            this.setOrderParking(orderParking);
            this.snackBar.open('Statusen för utcheckning är återställd', 'OK', {
              verticalPosition: 'top',
              duration: 2000
            });
            this.saveChangesEvent.emit();
            this.decrementRequests();
          },
            error => {
              this.decrementRequests();
              this.snackBar.open(`Felkod: ${error.status}`, 'Doh!', {
                verticalPosition: 'top'
              });
            });
      }
    });
  }

  setPaid() {
    const dialogRef = this.dialog.open(ConfirmDeleteComponent, {
      data: {
        message: `Vill du sätta status Paid på ${this.orderParkingVM.carId}?`,
        buttonText: {
          ok: 'Ja',
          cancel: 'Avbryt'
        }
      }
    });
    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.incrementRequests();
        this.parkingService
          .setStatusPaid(this.data.orderParkingId)
          .pipe(takeUntil(this.componentDestroyed$))
          .subscribe(orderParking => {
            this.setOrderParking(orderParking);
            this.snackBar.open('Statusen är ändrad till Paid', 'OK', {
              verticalPosition: 'top',
              duration: 2000
            });
            this.saveChangesEvent.emit();
            this.decrementRequests();
          },
            error => {
              this.decrementRequests();
              this.snackBar.open(`Felkod: ${error.status}`, 'Doh!', {
                verticalPosition: 'top'
              });
            });
      }
    });
  }

  sanitizeInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const sanitizedValue = input.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
    this.editOrderParkingForm.get('carId')?.setValue(sanitizedValue, { emitEvent: false });
    input.value = sanitizedValue;
  }

  ngOnDestroy() {
    this.componentDestroyed$.next(true);
    this.componentDestroyed$.complete();
  }
}
